import { Row, Col, Form } from 'react-bootstrap';
import { DateInput } from '../../../ui/DateInput/DateInput';
import { IFamilyMemberDto, ISystemCodeByTypeDto } from '../../../../utils/constants/interfaces';
import { useMemberInformation } from './useMemberInformation';
import styles from './MemberInformationSection.module.css';

interface MemberInformationSectionProps {
  member: IFamilyMemberDto;
  fieldErrors: string[];
  titleOptions: { value: number; label: string }[];
  suffixOptions: { value: number; label: string }[];
  memberStatusOptions: ISystemCodeByTypeDto[];
  loadingMemberStatuses: boolean;
  isPrimary: boolean;
  disabled?: boolean;
  onUpdate: <K extends keyof IFamilyMemberDto>(key: K, value: IFamilyMemberDto[K]) => void;
}

export const MemberInformationSection = ({
  member,
  fieldErrors,
  titleOptions,
  suffixOptions,
  memberStatusOptions,
  loadingMemberStatuses,
  isPrimary,
  disabled = false,
  onUpdate
}: MemberInformationSectionProps) => {
  const { errorPrefix } = useMemberInformation({ member, isPrimary });

  return (
    <div className={styles.card}>
      <h4 className={styles.sectionTitle}>Member Information</h4>

      {/* Personal Information */}
      <Row className="g-2 mb-2">
        <Col md={1}>
          <Form.Group className={styles.compactFormGroup}>
            <Form.Label>ID</Form.Label>
            <div className={styles.idValue}>{member.legacyMemberId || 'N/A'}</div>
          </Form.Group>
        </Col>
        <Col md={1}>
          <Form.Group className={styles.compactFormGroup}>
            <Form.Label>Title</Form.Label>
            <Form.Select
              size="sm"
              disabled={disabled}
              value={member.title !== null && member.title !== undefined ? member.title : ''}
              onChange={(e) =>
                onUpdate('title', e.target.value === '' ? null : Number(e.target.value))
              }>
              <option value="">- Select -</option>
              {titleOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
        <Col md={4}>
          <Form.Group className={styles.compactFormGroup}>
            <Form.Label
              className={
                fieldErrors.includes(`${errorPrefix}firstName`)
                  ? `${styles.required} is-invalid`
                  : styles.required
              }>
              First Name
            </Form.Label>
            <Form.Control
              size="sm"
              type="text"
              disabled={disabled}
              value={member.firstName}
              onChange={(e) => onUpdate('firstName', e.target.value)}
              isInvalid={fieldErrors.includes(`${errorPrefix}firstName`)}
            />
          </Form.Group>
        </Col>
        <Col md={2}>
          <Form.Group className={styles.compactFormGroup}>
            <Form.Label>Middle</Form.Label>
            <Form.Control
              size="sm"
              type="text"
              disabled={disabled}
              value={member.middleName}
              onChange={(e) => onUpdate('middleName', e.target.value)}
            />
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group className={styles.compactFormGroup}>
            <Form.Label
              className={
                fieldErrors.includes(`${errorPrefix}lastName`)
                  ? `${styles.required} is-invalid`
                  : styles.required
              }>
              Last Name
            </Form.Label>
            <Form.Control
              size="sm"
              type="text"
              disabled={disabled}
              value={member.lastName}
              onChange={(e) => onUpdate('lastName', e.target.value)}
              isInvalid={fieldErrors.includes(`${errorPrefix}lastName`)}
            />
          </Form.Group>
        </Col>
        <Col md={1}>
          <Form.Group className={styles.compactFormGroup}>
            <Form.Label>Suffix</Form.Label>
            <Form.Select
              size="sm"
              disabled={disabled}
              value={member.suffix !== null && member.suffix !== undefined ? member.suffix : ''}
              onChange={(e) =>
                onUpdate('suffix', e.target.value === '' ? null : Number(e.target.value))
              }>
              <option value="">-</option>
              {suffixOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
      </Row>

      <Row className="g-2 mb-2">
        <Col md={7}>
          <Form.Group className={styles.compactFormGroup}>
            <Form.Label>Email Address</Form.Label>
            <Form.Control
              size="sm"
              type="email"
              disabled={disabled}
              value={member.emailAddress}
              onChange={(e) => onUpdate('emailAddress', e.target.value)}
              isInvalid={fieldErrors.includes(`${errorPrefix}emailAddress`)}
            />
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group className={styles.compactFormGroup}>
            <Form.Label
              className={
                fieldErrors.includes(`${errorPrefix}memberStatusId`)
                  ? `${styles.required} is-invalid`
                  : styles.required
              }>
              Status
            </Form.Label>
            <Form.Select
              size="sm"
              disabled={disabled}
              value={member.memberStatusId || ''}
              onChange={(e) =>
                onUpdate('memberStatusId', e.target.value === '' ? 0 : Number(e.target.value))
              }
              isInvalid={fieldErrors.includes(`${errorPrefix}memberStatusId`)}>
              <option value="">- Select -</option>
              {memberStatusOptions.map((status) => (
                <option key={status.id} value={status.id}>
                  {status.description || status.codeValue}
                </option>
              ))}
            </Form.Select>
            {loadingMemberStatuses && <small className="text-muted">Loading...</small>}
          </Form.Group>
        </Col>
        <Col md={2}>
          <DateInput
            label="Birth Date"
            disabled={disabled}
            value={member.dateOfBirthUtc}
            onChange={(date) => onUpdate('dateOfBirthUtc', date)}
            isInvalid={fieldErrors.includes(`${errorPrefix}dateOfBirthUtc`)}
            required={isPrimary}
          />
        </Col>
      </Row>

      {/* Contact Information */}
      <h5 className={styles.subSectionTitle}>Contact & Address</h5>
      <Row className="g-2 mb-2">
        <Col md={4}>
          <Form.Group className={styles.compactFormGroup}>
            <Form.Label>Home Phone</Form.Label>
            <Form.Control
              size="sm"
              type="text"
              disabled={disabled}
              value={member.homePhone}
              onChange={(e) => onUpdate('homePhone', e.target.value)}
            />
          </Form.Group>
        </Col>
        <Col md={4}>
          <Form.Group className={styles.compactFormGroup}>
            <Form.Label>Cell Phone</Form.Label>
            <Form.Control
              size="sm"
              type="text"
              disabled={disabled}
              value={member.cellPhone}
              onChange={(e) => onUpdate('cellPhone', e.target.value)}
            />
          </Form.Group>
        </Col>
        <Col md={4}>
          <Form.Group className={styles.compactFormGroup}>
            <Form.Label>Profession</Form.Label>
            <Form.Control
              size="sm"
              type="text"
              disabled={disabled}
              value={member.profession}
              onChange={(e) => onUpdate('profession', e.target.value)}
            />
          </Form.Group>
        </Col>
      </Row>

      {/* Address */}
      <Row className="g-2 mb-2">
        <Col md={6}>
          <Form.Group className={styles.compactFormGroup}>
            <Form.Label>Address</Form.Label>
            <Form.Control
              size="sm"
              type="text"
              disabled={disabled}
              value={member.address}
              onChange={(e) => onUpdate('address', e.target.value)}
            />
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group className={styles.compactFormGroup}>
            <Form.Label>Address Line 2</Form.Label>
            <Form.Control
              size="sm"
              type="text"
              disabled={disabled}
              value={member.address2}
              onChange={(e) => onUpdate('address2', e.target.value)}
            />
          </Form.Group>
        </Col>
      </Row>

      <Row className="g-2">
        <Col md={6}>
          <Form.Group className={styles.compactFormGroup}>
            <Form.Label>City</Form.Label>
            <Form.Control
              size="sm"
              type="text"
              disabled={disabled}
              value={member.city}
              onChange={(e) => onUpdate('city', e.target.value)}
            />
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group className={styles.compactFormGroup}>
            <Form.Label>State</Form.Label>
            <Form.Control
              size="sm"
              type="text"
              disabled={disabled}
              value={member.state}
              onChange={(e) => onUpdate('state', e.target.value)}
              className={styles.stateInput}
            />
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group className={styles.compactFormGroup}>
            <Form.Label>Zip Code</Form.Label>
            <Form.Control
              size="sm"
              type="text"
              disabled={disabled}
              value={member.zipCode}
              onChange={(e) => onUpdate('zipCode', e.target.value)}
              className={styles.zipInput}
            />
          </Form.Group>
        </Col>
      </Row>
    </div>
  );
};
